import React, { useEffect, useState } from "react";
import { Scissors, Factory, ShoppingCart, Layers, TrendingUp } from "lucide-react";
import { api, fmtIDR } from "../../lib/api";
import { PageHeader, Panel } from "../../lib/ui";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";

const Stat = ({ label, value, sub }) => (
  <div className="bg-white border border-taupe/20 rounded-lg px-4 py-3" data-testid={`stat-${label}`}>
    <div className="text-xs text-wmuted">{label}</div>
    <div className="text-2xl font-heading text-cocoa mt-0.5">{value}</div>
    {sub && <div className="text-xs text-wmuted mt-0.5">{sub}</div>}
  </div>
);

const num = (n) => (Number(n) || 0).toLocaleString("id-ID");

export default function ProductionReport() {
  const [d, setD] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    api.get("/production/report").then((r) => setD(r.data)).catch(() => setErr(true));
  }, []);

  if (err) return <div className="p-6 text-wmuted" data-testid="report-error">Gagal memuat laporan.</div>;
  if (!d) return <div className="p-6 text-wmuted" data-testid="report-loading">Memuat laporan…</div>;

  return (
    <div className="space-y-6" data-testid="production-report">
      <PageHeader title="Laporan Produksi" subtitle="Ringkasan cutting, jahit/konveksi, pembelian material & master manufaktur" />

      <Panel className="p-5">
        <h3 className="font-heading text-cocoa flex items-center gap-2 mb-3"><Scissors className="w-5 h-5" /> Cutting</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Stat label="Total Cutting Order" value={num(d.cutting.count)} sub={`${num(d.cutting.selesai)} selesai`} />
          <Stat label="Target Potong (pcs)" value={num(d.cutting.target)} />
          <Stat label="Hasil Potong (pcs)" value={num(d.cutting.output)} />
          <Stat label="Total Biaya Cutting" value={fmtIDR(d.cutting.cost)} />
        </div>
      </Panel>

      <Panel className="p-5">
        <h3 className="font-heading text-cocoa flex items-center gap-2 mb-3"><Factory className="w-5 h-5" /> Order Jahit / Konveksi</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Stat label="Total Order Jahit" value={num(d.production.count)} sub={`${num(d.production.selesai)} selesai`} />
          <Stat label="Dikirim (pcs)" value={num(d.production.send)} />
          <Stat label="Diterima (pcs)" value={num(d.production.received)} />
          <Stat label="Total Biaya Jahit" value={fmtIDR(d.production.cost)} />
        </div>
      </Panel>

      <Panel className="p-5">
        <h3 className="font-heading text-cocoa flex items-center gap-2 mb-3"><ShoppingCart className="w-5 h-5" /> Pembelian Material</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Stat label="Total PO Material" value={num(d.purchase.count)} sub={`${num(d.purchase.paid)} lunas`} />
          <Stat label="Total Nilai Pembelian" value={fmtIDR(d.purchase.total)} />
          <Stat label="Kain (master)" value={num(d.masters.fabrics)} />
          <Stat label="Aksesoris (master)" value={num(d.masters.accessories)} />
        </div>
      </Panel>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Panel className="p-5">
          <h3 className="font-heading text-cocoa flex items-center gap-2 mb-3"><TrendingUp className="w-5 h-5" /> Top Produk (Hasil Jahit)</h3>
          <Table>
            <TableHeader><TableRow className="hover:bg-transparent"><TableHead>Produk</TableHead><TableHead className="text-right">Diterima (pcs)</TableHead></TableRow></TableHeader>
            <TableBody>
              {d.top_products.map((p, i) => (
                <TableRow key={i} data-testid={`top-product-${i}`}><TableCell className="text-cocoa">{p.product}</TableCell><TableCell className="text-right font-medium">{num(p.received)}</TableCell></TableRow>
              ))}
              {!d.top_products.length && <TableRow><TableCell colSpan={2} className="text-center text-wmuted py-6">Belum ada data.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </Panel>

        <Panel className="p-5">
          <h3 className="font-heading text-cocoa flex items-center gap-2 mb-3"><Layers className="w-5 h-5" /> Top Konveksi/Penjahit</h3>
          <Table>
            <TableHeader><TableRow className="hover:bg-transparent"><TableHead>Mitra</TableHead><TableHead className="text-right">Jml Order</TableHead></TableRow></TableHeader>
            <TableBody>
              {d.top_tailors.map((t, i) => (
                <TableRow key={i} data-testid={`top-tailor-${i}`}><TableCell className="text-cocoa">{t.partner}</TableCell><TableCell className="text-right font-medium">{num(t.orders)}</TableCell></TableRow>
              ))}
              {!d.top_tailors.length && <TableRow><TableCell colSpan={2} className="text-center text-wmuted py-6">Belum ada data.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </Panel>
      </div>
    </div>
  );
}
